<!-- eslint-disable @typescript-eslint/no-explicit-any -->
<!--
 * COPYRIGHT Transtron Inc 2022
 *
 * 機能名：管理対象マスタ画面
 *
 * 作成者：2021/11/19 ノア）賀
 * 最終修正者：2022/1/20 ノア）黄
 *
 -->
<template>
  <div class="tti-main-view">
    <!-- ヘッダー -->
    <section>
      <nav class="tti-main-header-shadow">
        <div class="pull-left">
          <el-button class="backButton" @click="onBackButtonClick" v-tooltip="getTooltip(tooltipType.back)">
            <tti-svg-icon icon-name="wo_034"></tti-svg-icon>
          </el-button>
          <div class="tti-viewTitle">
            <tti-svg-icon icon-name="wo_008"></tti-svg-icon>
            <span>{{ $t('master.managementTargetMaster.viewTitle', [managementTargetShowName]) }}</span>
          </div>
        </div>
        <div class="pull-right">
          <!-- 連結可能トラクターヘッドマスタ -->
          <el-button
            class="tti-btn-jump"
            @click="onJumpToTrailerSetClick"
            v-tooltip="{
              content: `${$t('master.managementTargetMaster.linkButtonTip')}`,
              placement: 'bottom-start',
              delay: { show: 1000, hide: 0 }
            }"
          >
            <tti-svg-icon icon-name="wo_008"></tti-svg-icon>
            {{ $t('master.managementTargetMaster.linkButton') }}
            <tti-svg-icon icon-name="wo_035"></tti-svg-icon>
          </el-button>

          <!-- 連結可能連携会社マスタ -->
          <el-button
            class="tti-btn-jump"
            @click="onJumpToCompanySetClick"
            v-tooltip="{
              content: `${$t('master.managementTargetMaster.linkCompanyButtonTip')}`,
              placement: 'bottom-start',
              delay: { show: 1000, hide: 0 }
            }"
            v-if="showFlag"
          >
            <tti-svg-icon icon-name="wo_008"></tti-svg-icon>
            {{ $t('master.managementTargetMaster.linkCompanyButton') }}
            <tti-svg-icon icon-name="wo_035"></tti-svg-icon>
          </el-button>
        </div>
      </nav>
    </section>
    <!-- テーブル -->
    <section class="tti-padding-content">
      <div class="tti-content-title-container">
        <div class="tti-content-title">
          <span class="contentTitle">{{
            $t('master.managementTargetMaster.contentTitle', [managementTargetShowName])
          }}</span>
          <el-select
            class="tti-viewSelect"
            v-model="businessOffice"
            size="small"
            :placeholder="$t('master.viewTitle.placeHolderOfOfficeSelector')"
            @change="onBusinessOfficeChange"
            v-tooltip="{
              content: `${$t('master.viewTitle.placeHolderOfOfficeSelector')}`,
              placement: 'bottom-start',
              delay: { show: 1000, hide: 0 }
            }"
          >
            <el-option
              v-for="item in businessOfficeMasterData"
              :key="item.id"
              :label="getBusinessOfficeLabel(item)"
              :value="item.id"
            ></el-option>
          </el-select>
          <span>{{ tableData.length }}</span>
          <span class="subject">{{ $t('master.viewTitle.subject') }}</span>
        </div>
        <div class="functionArea">
          <el-pagination
            background
            layout="prev, pager, next"
            :total="tableData.length"
            :page-size="visablePageSize"
            :hide-on-single-page="true"
            :current-page.sync="currentPage"
            @current-change="onCurrentPageChange"
          >
          </el-pagination>
          <div class="searchBox">
            <el-input v-model.trim="searchText" onkeyup="this.value=this.value.replace(/[,]/g,'')">
              <el-button slot="append" icon="el-icon-search" @click="onSearchBoxClick"></el-button>
            </el-input>
          </div>
        </div>
      </div>
      <el-form :model="form" ref="form" :disabled="pageLock">
        <el-table
          ref="tableRef"
          :data="visableTableData"
          height="calc(100vh - 226px)"
          border
          :stripe="true"
          header-row-class-name="tti-master-table-header"
          :empty-text="$t('messages.common.emptyTableData')"
          :row-class-name="tableRowClassName"
          highlight-current-row
          @current-change="handleCurrentChange"
          @selection-change="handleSelectionChange"
          @sort-change="handleSortChange"
        >
          <el-table-column type="selection" width="35"></el-table-column>
          <!-- 管理対象コード -->
          <el-table-column
            class-name="required"
            prop="managementTargetCode"
            :label="$t('master.managementTargetMaster.managementTargetCode', [managementTargetShowName])"
            width="190"
            :sortable="true"
          >
            <template slot-scope="scope">
              <el-form-item
                :prop="'tableData[' + scope.$index + '].managementTargetCode'"
                :rules="rules.managementTargetCode"
              >
                <el-input
                  :id="'tableData[' + scope.$index + '].managementTargetCode'"
                  v-model.trim="scope.row.managementTargetCode"
                  size="small"
                  :disabled="!canCodeChange(scope.row.dataType)"
                  @change="onRepeatItemChange(formRef, 'managementTargetCode')"
                ></el-input>
              </el-form-item>
            </template>
          </el-table-column>
          <!-- 管理対象名 -->
          <el-table-column
            class-name="required"
            prop="trailerName"
            :label="$t('master.managementTargetMaster.managementTargetName', [managementTargetShowName])"
            width="300"
            :sortable="true"
          >
            <template slot-scope="scope">
              <el-form-item
                :prop="'tableData[' + scope.$index + '].managementTargetName'"
                :rules="rules.managementTargetName"
              >
                <el-input
                  v-model.trim="scope.row.managementTargetName"
                  :id="'tableData[' + scope.$index + '].managementTargetName'"
                  size="small"
                ></el-input>
              </el-form-item>
            </template>
          </el-table-column>
          <!-- sigfoxId -->
          <el-table-column
            prop="sigfoxId"
            :label="$t('master.managementTargetMaster.sigfoxId')"
            width="300"
            :sortable="true"
          >
            <template slot-scope="scope">
              <el-form-item :prop="'tableData[' + scope.$index + '].sigfoxId'" :rules="rules.sigfoxId">
                <el-select
                  v-model="scope.row.sigfoxId"
                  size="small"
                  filterable
                  clearable
                  @change="onRepeatItemChange(formRef, 'sigfoxId')"
                >
                  <el-option
                    v-for="item in gpsTrackerMasterData"
                    :key="item.id"
                    :label="item.sigfoxId"
                    :value="item.id"
                  ></el-option>
                </el-select>
              </el-form-item>
            </template>
          </el-table-column>
          <!-- 利用状況 -->
          <el-table-column
            class-name="required"
            prop="usageStatus"
            :label="$t('master.managementTargetMaster.usageStatus')"
            width="120"
            :sortable="true"
          >
            <template slot-scope="scope">
              <el-form-item :prop="'tableData[' + scope.$index + '].usageStatus'" :rules="rules.usageStatus">
                <el-select
                  :id="'tableData[' + scope.$index + '].usageStatus'"
                  v-model="scope.row.usageStatus"
                  size="small"
                >
                  <el-option
                    v-for="item in usageStatusList"
                    :key="item.code"
                    :label="item.name"
                    :value="item.code"
                  ></el-option>
                </el-select>
              </el-form-item>
            </template>
          </el-table-column>
          <!-- 所有者 -->
          <el-table-column prop="owner" :label="$t('master.managementTargetMaster.owner')" width="200" :sortable="true">
            <template slot-scope="scope">
              <el-form-item :prop="'tableData[' + scope.$index + '].owner'" :rules="rules.owner">
                <el-input v-model.trim="scope.row.owner" size="small"></el-input>
              </el-form-item>
            </template>
          </el-table-column>
          <!-- 貸出先営業所コード -->
          <el-table-column
            prop="lendBusinessOfficeId"
            :label="$t('master.managementTargetMaster.lendBusinessOfficeCode')"
            width="218"
            :sortable="true"
          >
            <template slot-scope="scope">
              <el-form-item
                :prop="'tableData[' + scope.$index + '].lendBusinessOfficeId'"
                :rules="rules.lendBusinessOfficeId"
              >
                <el-select v-model="scope.row.lendBusinessOfficeId" clearable size="small">
                  <el-option
                    v-for="item in getLendBusinessOfficeList(scope.row.lendBusinessOfficeId)"
                    :key="item.id"
                    :label="getLendBusinessOfficeLabel(item)"
                    :value="item.id"
                  ></el-option> </el-select
              ></el-form-item>
            </template>
          </el-table-column>
          <!-- 貸出日 -->
          <el-table-column
            prop="lendDate"
            :label="$t('master.managementTargetMaster.lendDate')"
            width="170"
            :sortable="true"
          >
            <template slot-scope="scope">
              <el-form-item :prop="'tableData[' + scope.$index + '].lendDate'" :rules="rules.lendDate">
                <el-date-picker
                  v-model="scope.row.lendDate"
                  type="date"
                  format="yyyy/MM/dd"
                  value-format="yyyy/MM/dd"
                  size="small"
                  :editable="false"
                  clearable
                ></el-date-picker>
              </el-form-item>
            </template>
          </el-table-column>
          <!-- 次回定期整備時期日 -->
          <el-table-column
            prop="nextArrangementDate"
            :label="$t('master.managementTargetMaster.nextArrangementDate')"
            width="170"
            :sortable="true"
          >
            <template slot-scope="scope">
              <el-form-item :prop="'tableData[' + scope.$index + '].nextArrangementDate'">
                <el-date-picker
                  v-model="scope.row.nextArrangementDate"
                  type="date"
                  format="yyyy/MM/dd"
                  value-format="yyyy/MM/dd"
                  size="small"
                  :editable="false"
                  clearable
                  :sortable="true"
                ></el-date-picker>
              </el-form-item>
            </template>
          </el-table-column>
          <!-- 次回車検期日 -->
          <el-table-column
            prop="nextCheckDate"
            :label="$t('master.managementTargetMaster.nextCheckDate')"
            width="170"
            :sortable="true"
          >
            <template slot-scope="scope">
              <el-form-item :prop="'tableData[' + scope.$index + '].nextCheckDate'">
                <el-date-picker
                  v-model="scope.row.nextCheckDate"
                  type="date"
                  format="yyyy/MM/dd"
                  value-format="yyyy/MM/dd"
                  size="small"
                  :editable="false"
                  clearable
                ></el-date-picker>
              </el-form-item>
            </template>
          </el-table-column>
          <!-- 利用可能エリアと都道府県 -->
          <el-table-column
            prop="areaId"
            :label="$t('master.managementTargetMaster.area')"
            width="700"
            :sortable="false"
          >
            <template slot-scope="scope">
              <div class="areaContainer">
                <el-button
                  class="areaInsert tti-btn-003"
                  type="text"
                  size="mini"
                  v-tooltip="getTooltip('areaInsert')"
                  v-if="!scope.row.areaList || scope.row.areaList.length === 0"
                  @click="onAreaInsertClick(scope.row)"
                >
                  <tti-svg-icon icon-name="su_011"></tti-svg-icon>
                </el-button>
                <el-table
                  :data="scope.row.areaList"
                  border
                  :empty-text="$t('messages.common.emptyTableData')"
                  :show-header="false"
                  cell-class-name="areaTable"
                >
                  <!-- 利用可能エリア -->
                  <el-table-column width="240">
                    <template slot-scope="areaScope">
                      <div class="areaContainer">
                        <el-button
                          class="areaInsert tti-btn-003"
                          type="text"
                          size="mini"
                          v-tooltip="getTooltip('areaInsert')"
                          :disabled="isAreaInsertDisabled(scope.row, areaScope.row)"
                          @click="onAreaInsertClick(scope.row, areaScope.row, areaScope.$index)"
                        >
                          <tti-svg-icon icon-name="su_011"></tti-svg-icon>
                        </el-button>
                        <el-button
                          class="areaInsert tti-btn-003"
                          type="text"
                          size="mini"
                          v-tooltip="getTooltip('areaDelete')"
                          @click="onAreaDeleteClick(scope.row, areaScope.$index)"
                        >
                          <tti-svg-icon icon-name="su_013"></tti-svg-icon>
                        </el-button>
                        <el-select v-model="areaScope.row.areaId" size="mini">
                          <el-option
                            v-for="item in getSeletableArea(scope.row, areaScope.row)"
                            :key="item.id"
                            :label="item.areaName"
                            :value="item.id"
                          >
                          </el-option>
                        </el-select>
                      </div>
                    </template>
                  </el-table-column>
                  <!-- 利用可能都道府県 -->
                  <el-table-column>
                    <template slot-scope="areaScope">
                      {{ getPrefectureLayout(areaScope.row) }}
                    </template>
                  </el-table-column>
                </el-table>
              </div>
            </template>
          </el-table-column>
          <!-- デジタコ連携会社 -->
          <el-table-column
            prop="connectCompany"
            :label="$t('master.managementTargetMaster.connectCompany')"
            width="540"
            :sortable="false"
            v-if="showFlag"
          >
            <template slot-scope="scope">
              <div class="connectContainer">
                <el-form-item>
                  <el-select
                    v-model="scope.row.itpConnectCompanyId"
                    size="small"
                    filterable
                    clearable
                    class="select-connect-company"
                  >
                    <el-option
                      v-for="item in connectCompanyMasterData"
                      :key="item.id"
                      :label="item.companyName"
                      :value="item.id"
                    ></el-option>
                  </el-select>
                </el-form-item>
                <div class="company-code">{{ getCompanyCodeById(scope.row.itpConnectCompanyId) }}</div>
              </div>
            </template>
          </el-table-column>
        </el-table>
      </el-form>
    </section>
    <!-- フッター -->
    <section class="tti-master-bottom-area">
      <div class="tti-bottom-left-area">
        <!-- 取消 -->
        <el-button
          class="tti-btn-default tti-btn-001"
          @click="onCancelButtonClick"
          :disabled="pageLock"
          v-tooltip="getTooltip(tooltipType.cancel)"
          ><tti-svg-icon icon-name="su_005"></tti-svg-icon>{{ $t('buttons.cancel') }}</el-button
        >
        <!-- 削除 -->
        <el-button
          class="tti-btn-default tti-btn-005"
          @click="onDeleteButtonClick"
          :disabled="pageLock"
          v-tooltip="getTooltip(tooltipType.delete)"
          ><tti-svg-icon icon-name="su_013"></tti-svg-icon>{{ $t('buttons.delete') }}</el-button
        >
        <!-- コピー -->
        <el-button
          class="tti-btn-default tti-btn-005"
          @click="onCopyButtonClick"
          :disabled="pageLock"
          v-tooltip="getTooltip(tooltipType.copy)"
        >
          <tti-svg-icon icon-name="su_012"></tti-svg-icon>{{ $t('buttons.copy') }}
        </el-button>
        <!-- 追加 -->
        <el-button
          class="tti-btn-default tti-btn-005"
          @click="onInsertButtonClick"
          :disabled="pageLock"
          v-tooltip="getTooltip(tooltipType.insert)"
        >
          <tti-svg-icon icon-name="su_011"></tti-svg-icon>{{ $t('buttons.insert') }}
        </el-button>
        <!-- テンプレート -->
        <el-button
          class="tti-btn-default tti-btn-006"
          @click="onTamplateButtonClick"
          :disabled="pageLock"
          v-tooltip="getTooltip(tooltipType.template)"
        >
          {{ $t('buttons.template') }}
        </el-button>

        <!-- デジタコ連携会社テンプレート -->
        <el-button
          class="tti-btn-default tti-btn-006 connect-template"
          @click="onTemplateButtonClick"
          :disabled="pageLock"
          v-tooltip="getTooltip(tooltipType.template)"
          v-if="showFlag"
        >
          {{ $t('buttons.connectTemplate') }}
        </el-button>
        <!-- デジタコ連携の反映 -->
        <el-button
          class="tti-btn-default tti-btn-005 connect-upload"
          @click="onInputButtonClick2(true)"
          :disabled="pageLock"
          v-tooltip="getTooltip(tooltipType.input)"
          v-if="showFlag"
        >
          <tti-svg-icon icon-name="su_004"></tti-svg-icon>{{ $t('buttons.connectUpload') }}
        </el-button>
        <!-- デジタコ連携の出力 -->
        <el-button
          class="tti-btn-default tti-btn-005 connect-upload"
          @click="onOutputButtonClick2(true)"
          :disabled="pageLock"
          v-tooltip="getTooltip(tooltipType.output)"
          v-if="showFlag"
        >
          <tti-svg-icon icon-name="su_003"></tti-svg-icon>{{ $t('buttons.connectOutput') }}
        </el-button>
      </div>
      <div class="tti-bottom-right-area">
        <!-- 入力 -->
        <el-button
          class="tti-btn-default tti-btn-001"
          @click="onInputButtonClick2(false)"
          :disabled="pageLock"
          v-tooltip="getTooltip(tooltipType.input)"
        >
          <tti-svg-icon icon-name="su_004"></tti-svg-icon>{{ $t('buttons.input') }}
        </el-button>
        <!-- 出力 -->
        <el-button
          class="tti-btn-default tti-btn-001"
          @click="onOutputButtonClick2(false)"
          :disabled="pageLock"
          v-tooltip="getTooltip(tooltipType.output)"
        >
          <tti-svg-icon icon-name="su_003"></tti-svg-icon>{{ $t('buttons.output') }}
        </el-button>
        <!-- 保存 -->
        <el-button
          class="tti-btn-default tti-btn-001"
          @click="onSaveButtonClick"
          :disabled="pageLock"
          v-tooltip="getTooltip(tooltipType.save)"
        >
          <tti-svg-icon icon-name="su_002"></tti-svg-icon>{{ $t('buttons.save') }}
        </el-button>
        <!-- file -->
        <input ref="inputFile" type="file" @change="onFileChange" class="fileInput" accept=".csv" />
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import { Component, Ref } from 'vue-property-decorator'
import { Form, Table } from 'element-ui'
import { cloneDeep, find, findIndex, findLastIndex, isEmpty } from 'lodash'

import MST_BASE, { FormItemType } from '@/views/master/MST_BASE.vue'
import { i18n } from '@/locales'
import {
  getAreaMasterDataWhoHasSubPrefecture,
  getGPSTrackerMasterData,
  getManagementTargetMasterData,
  updateManagementTargetMasterData,
  managementTargetCSVDownload,
  getConnectCompanyMasterData,
  templateDownload
} from '@/store/apis'
import {
  BusinessOfficeMasterData,
  ManagementTargetMasterData,
  AreaMasterData,
  General,
  MasterData,
  PrefectureMasterData,
  GPSTrackerMasterData,
  ManagementTargetArea,
  CommonResponse,
  ConnectCompanyMasterData
} from '@/models'
import { DataType, TemplateCode, MasterType, UsageStatus } from '@/commons/constants'
import config from '@/config/config'
import { localService } from '@/commons/services/localService'
import { generalService, GeneralType } from '@/commons/services/generalService'
import { RULES, VALID_PATTERN, validateService } from '@/service/validateService'
import HelperService from '@/service/helperService'
import { msgBoxService, MessageBoxOption } from '@/commons/services/msgBoxService'
import { moveService } from '@/commons/services/moveService'
import { common } from '@/store/modules'
import { loadingService } from '@/commons/services/loadingService'

/**
 * 管理対象マスタ画面
 *
 * @class
 */
@Component({
  name: 'mst-mgmt-trgts'
})
export default class MST_MGMT_TRGTS extends MST_BASE {
  /**
   * フォームのref
   */
  @Ref('form') readonly formRef!: Form

  @Ref('tableRef') readonly tableRef!: Table

  /**
   * 画面ID
   */
  pageId = 'MST_MGMT_TRGTS'

  /**
   * 第一列の項目（Focus用）
   */
  firstField = 'managementTargetCode'

  /**
   * 画面名
   */
  pageName = i18n.t('master.managementTargetMaster.viewTitle', [this.managementTargetShowName]).toString()

  /**
   * 最先のCSVヘッダー
   */
  firstCsvHeader = i18n.t('master.csvHeader.MST_MGMT_TRGTS').toString()

  /**
   * テンプレートコード
   */
  templateCode = TemplateCode.MST_MGMT_TRGTS

  /**
   * エリア割当リスト
   */
  areaAllocationMasterData: AreaMasterData[] = []

  /**
   * 都道府県
   */
  prefectureMasterData: PrefectureMasterData[] = []

  /**
   * GPSトラッカーマスタリスト
   */
  gpsTrackerMasterData: GPSTrackerMasterData[] = []

  /**
   * 連携会社リスト
   */
  connectCompanyMasterData: ConnectCompanyMasterData[] = []

  /**
   * 利用状況
   */
  usageStatusList: General[] = []

  /**
   * 管理対象マスタ編集FORM
   */
  form = {
    tableData: this.tableData
  } as Record<string, ManagementTargetMasterData[]>

  /**
   * デジタコ連携フラグ
   */
  showFlag = localService.itpConnectValidFlag === '1'
  /**
   * 営業所選択で選択された営業所リスト
   *
   * @returns {BusinessOfficeMasterData[]} 営業所リスト
   */
  get allocedBusinessOfficeList(): BusinessOfficeMasterData[] {
    return this.businessOfficeMasterData.filter(item => item.parentId)
  }

  /**
   * 貸出先営業所項目の選択できる営業所リストを取得する
   *
   * @param {string} id 当前データの営業所ID
   * @returns {BusinessOfficeMasterData[]} 営業所リスト
   */
  getLendBusinessOfficeList(id: string): BusinessOfficeMasterData[] {
    const baseOfficeList = cloneDeep(this.allocedBusinessOfficeList).filter(item => item.id !== this.businessOffice)
    if (!id) {
      return baseOfficeList
    }

    if (findIndex(baseOfficeList, { id }) >= 0) {
      return baseOfficeList
    }

    const targetOffice = find(this.businessOfficeMasterDataCache, { id })
    if (targetOffice) {
      baseOfficeList.push(targetOffice)
    }

    return baseOfficeList
  }

  /**
   * 検証ルール
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rules = {} as any

  /**
   * CSVヘッダーリスト
   */
  csvHeaders = [] as string[]

  // eslint-disable-next-line max-lines-per-function
  initRule(): void {
    this.rules = {
      // 管理対象コード
      managementTargetCode: [
        // 必須
        {
          required: true,
          message: i18n
            .t('vaildate.common.required', [
              i18n.t('master.managementTargetMaster.managementTargetCode', [this.managementTargetShowName])
            ])
            .toString(),
          csvMessage: 'vaildate.csvFile.required',
          trigger: 'blur'
        },
        // 半角英数字
        {
          pattern: VALID_PATTERN[RULES.ALPHANUMERIC],
          message: i18n
            .t('vaildate.common.alphanumericWithLength', [
              i18n.t('master.managementTargetMaster.managementTargetCode', [this.managementTargetShowName]),
              15
            ])
            .toString(),
          trigger: 'blur'
        },
        {
          validRule: [
            // 15文字
            {
              rule: RULES.MAXLENGTH,
              number: 15,
              message: i18n
                .t('vaildate.common.maxlength', [
                  i18n.t('master.managementTargetMaster.managementTargetCode', [this.managementTargetShowName]),
                  15
                ])
                .toString()
            },
            // 重複
            {
              rule: RULES.REPEAT,
              tableData: this.tableData,
              visableTableData: () => this.visableTableData,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              isDisabled: (row: any) => !this.canCodeChange(row.dataType),
              tableColumn: 'managementTargetCode',
              message: i18n
                .t('vaildate.common.repeat', [
                  i18n.t('master.managementTargetMaster.managementTargetCode', [this.managementTargetShowName])
                ])
                .toString()
            }
          ],
          trigger: 'blur'
        }
      ],
      // 管理対象名
      managementTargetName: [
        // 必須
        {
          required: true,
          message: i18n
            .t('vaildate.common.required', [
              i18n.t('master.managementTargetMaster.managementTargetName', [this.managementTargetShowName])
            ])
            .toString(),
          trigger: 'blur'
        },
        {
          validRule: [
            // 50文字
            {
              rule: RULES.MAXLENGTH,
              number: 50,
              message: i18n
                .t('vaildate.common.maxlength', [
                  i18n.t('master.managementTargetMaster.managementTargetName', [this.managementTargetShowName]),
                  50
                ])
                .toString()
            }
          ],
          trigger: 'blur'
        }
      ],
      // sigfoxId
      sigfoxId: [
        {
          validRule: [
            // 重複
            {
              rule: RULES.REPEAT,
              tableData: this.tableData,
              tableColumn: 'sigfoxId',
              message: i18n.t('vaildate.common.repeat', [i18n.t('master.managementTargetMaster.sigfoxId')]).toString()
            }
          ],
          trigger: 'change'
        }
      ],
      // 利用状況
      usageStatus: [
        // 必須
        {
          required: true,
          message: i18n.t('vaildate.common.required', [i18n.t('master.managementTargetMaster.usageStatus')]).toString(),
          trigger: 'change'
        }
      ],
      // 所有者
      owner: [
        {
          validRule: [
            // 50文字
            {
              rule: RULES.MAXLENGTH,
              number: 50,
              message: i18n
                .t('vaildate.common.maxlength', [i18n.t('master.managementTargetMaster.owner'), 50])
                .toString(),
              csvMessage: 'vaildate.csvFile.numberWithLength',
              messageEnterPoint: [i18n.t('master.managementTargetMaster.owner').toString(), 50]
            }
          ],
          trigger: 'blur'
        }
      ],
      //貸出先営業所コード
      lendBusinessOfficeId: [
        {
          validator: validateService.getValidator([
            // 複雑さチェック
            {
              rule: RULES.CUSTOM,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              callback: (value: string, rule: any): string | undefined => {
                const currentRowIndex = validateService.getCurrentRowIndex(rule)
                const currentRow = this.visableTableData[currentRowIndex]

                if (!value && !currentRow.lendDate) {
                  this.formRef.clearValidate(['tableData[' + currentRowIndex + '].lendDate'])
                }

                if (!value && currentRow.lendDate) {
                  const message = i18n
                    .t('vaildate.common.required', [i18n.t('master.managementTargetMaster.lendBusinessOfficeCode')])
                    .toString()
                  return message
                }
              }
            }
          ]),
          trigger: 'change'
        }
      ],
      //貸出日
      lendDate: [
        {
          validator: validateService.getValidator([
            // 複雑さチェック
            {
              rule: RULES.CUSTOM,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              callback: (value: string, rule: any): string | undefined => {
                const currentRowIndex = validateService.getCurrentRowIndex(rule)
                const currentRow = this.visableTableData[currentRowIndex]

                if (!value && !currentRow.lendBusinessOfficeId) {
                  this.formRef.clearValidate(['tableData[' + currentRowIndex + '].lendBusinessOfficeId'])
                }

                if (!value && currentRow.lendBusinessOfficeId) {
                  const message = i18n
                    .t('vaildate.common.required', [i18n.t('master.managementTargetMaster.lendDate')])
                    .toString()
                  return message
                }
              }
            }
          ]),
          trigger: 'change'
        }
      ]
    }
  }

  /**
   * インスタンスがマウントされたちょうど後にフック処理
   *
   * @returns {void}
   */
  async mounted(): Promise<void> {
    this.$log.debug('MST_MGMT_TRGTS > mounted')
    this.initFormTemplate()
    await this.fetchBusinessOfficeMaster()
    this.fetchAreaAllocationMasterData()
    this.fetchGPSTrackerMasterData()
    this.fetchUsageStatus()
    if (this.showFlag) {
      await this.fetchConnectMasterData()
    }
    this.initFormRef(this.formRef)
    this.initTableRef(this.tableRef)
    this.initSearchTargets()
  }

  protected fetchTableDataBefore(): () => Promise<MasterData> {
    return () => {
      const params = {
        type: MasterType.DETAIL,
        businessOfficeId: this.businessOffice
      }
      return getManagementTargetMasterData(params)
    }
  }

  fetchTableDataAfter(): void {
    this.initRule()
    this.initValidate(this.rules)
    this.multipleSelection = []
  }
  /**
   * クリックしたのはITP連携関連ボタンかどうか
   * デジタコ連携：true
   */
  itpFlag = false

  fetchCsvHeader(): void {
    if (this.showFlag && this.itpFlag) {
      this.csvHeaders = [
        'managementTargetCode',
        'managementTargetName',
        'sigfoxId',
        'usageStatus',
        'owner',
        'lendBusinessOfficeCode',
        'lendDate',
        'nextArrangementDate',
        'nextCheckDate',
        'area',
        'prefecture',
        'companyName'
      ] as string[]
    } else {
      this.csvHeaders = [
        'managementTargetCode',
        'managementTargetName',
        'sigfoxId',
        'usageStatus',
        'owner',
        'lendBusinessOfficeCode',
        'lendDate',
        'nextArrangementDate',
        'nextCheckDate',
        'area',
        'prefecture'
      ] as string[]
    }
  }

  /**
   * 入力ボタンをクリックした時の処理。
   *
   * @param {boolean} flag フラグ
   * @returns {void}
   */
  async onInputButtonClick2(flag: boolean): Promise<void> {
    this.$log.debug(`MST_BASE > ${this.pageId} > onInputButtonClick2`)
    this.itpFlag = flag
    this.fetchCsvHeader()
    if (!this.checkDataChanged()) {
      this.handleInputFile()
      return
    }

    const confirmCallback = async () => {
      try {
        if (!(await this.saveTableData())) {
          return
        }
      } catch (e) {
        return
      }

      this.handleInputFile()
    }

    const rejectCallback = () => {
      this.handleInputFile()
    }

    msgBoxService.confirmWithCancel(
      i18n.t('messages.masterCommon.saveConfirmWhenChanged').toString(),
      confirmCallback,
      rejectCallback
    )
  }

  /**
   * 出力ボタンをクリックした時の処理。
   *
   * @param {boolean} flag フラグ
   * @returns {void}
   */
  async onOutputButtonClick2(flag: boolean): Promise<void> {
    this.$log.debug(`MST_BASE > ${this.pageId} > onOutputButtonClick`)
    this.itpFlag = flag
    if (!this.checkDataChanged()) {
      await this.doTableDataOutput()
      return
    }

    const confirmCallback = async () => {
      try {
        if (!(await this.saveTableData())) {
          return
        }
      } catch (e) {
        return
      }

      this.doTableDataOutput()
    }

    const rejectCallback = async () => {
      await this.doTableDataOutput()
    }

    msgBoxService.confirmWithCancel(
      i18n.t('messages.masterCommon.saveConfirmWhenChanged').toString(),
      confirmCallback,
      rejectCallback
    )
  }

  /**
   * 貸出先営業所コードの表示名を取得する
   *
   * @param {BusinessOfficeMasterData} row 営業所データ
   * @returns {string} 表示名
   */
  getLendBusinessOfficeLabel(row: BusinessOfficeMasterData): string {
    return `${row.businessOfficeName}(${row.businessOfficeCode})`
  }

  /**
   * エリアを追加できるかどうかの判断
   *
   * @param {ManagementTargetMasterData} data 選択した管理対象
   * @param {ManagementTargetArea} currentArea 選択したエリア
   * @returns {boolean}追加できるかどうか
   */
  isAreaInsertDisabled(data: ManagementTargetMasterData, currentArea: ManagementTargetArea): boolean {
    const selectableArea = this.getSeletableArea(data, currentArea)
    if (selectableArea.length <= 1) {
      return true
    }

    return false
  }

  /**
   * エリアを追加する
   *
   * @param {ManagementTargetMasterData} data 選択した管理対象
   * @param {ManagementTargetArea} currentArea 選択したエリア
   * @param {number} index 選択したエリアのインデックス
   * @returns {void}
   */
  onAreaInsertClick(data: ManagementTargetMasterData, currentArea?: ManagementTargetArea, index?: number): void {
    const selectableArea = this.getSeletableArea(data, currentArea || ({} as ManagementTargetArea))

    if (!currentArea || index === undefined) {
      data.areaList = [
        {
          areaId: selectableArea[0].id
        }
      ]
      return
    }

    let addAreaId
    if (selectableArea.length <= 1) {
      return
    }

    if (selectableArea[0].id === currentArea.areaId) {
      addAreaId = selectableArea[1].id
    } else {
      addAreaId = selectableArea[0].id
    }

    data.areaList.splice(index + 1, 0, {
      areaId: addAreaId
    })
  }

  /**
   * エリアを削除する
   *
   * @param {ManagementTargetMasterData} data 選択した管理対象
   * @param {number} index 選択したエリアのインデックス
   * @returns {void}
   */
  onAreaDeleteClick(data: ManagementTargetMasterData, index: number): void {
    data.areaList.splice(index, 1)
  }

  /**
   * 選択できるエリアリストを取得する
   *
   * @param {ManagementTargetMasterData} data 選択した管理対象
   * @param {ManagementTargetArea} currentArea 選択したエリア
   * @returns {AreaMasterData[]} 選択できるエリアリスト
   */
  getSeletableArea(data: ManagementTargetMasterData, currentArea: ManagementTargetArea): AreaMasterData[] {
    const areaList: AreaMasterData[] = []
    const areaData = data.areaList
    if (!areaData || areaData.length === 0) {
      return this.areaAllocationMasterData
    }

    this.areaAllocationMasterData.forEach(item => {
      if (
        0 > findIndex(areaData as ManagementTargetArea[], (area: ManagementTargetArea) => area.areaId === item.id) ||
        currentArea.areaId === item.id
      ) {
        areaList.push(item)
      }
    })

    return areaList
  }

  /**
   * 選択できる都道府県リストを取得する
   *
   * @param {ManagementTargetArea} data エリア
   * @returns {AreaMasterData[]} 選択できるエリアリスト
   */
  getPrefectureLayout(data: ManagementTargetArea): string {
    const areaId = data.areaId
    const prefectureList = this.getPrefecturesByAreaId(areaId)
    return prefectureList.map(item => item.prefectureName).join(',')
  }

  /**
   * 利用状況リストを取得する
   *
   * @returns {void}
   */
  async fetchUsageStatus(): Promise<void> {
    try {
      this.usageStatusList = await generalService[GeneralType.USAGE_STATUS]
    } catch (e) {
      this.$log.debug('MST_MGMT_TRGTS > fetchUsageStatus: e=', e)
      throw e
    }
  }

  /**
   * GPSトラッカーマスタを取得する。
   *
   * @returns {void}
   */
  async fetchGPSTrackerMasterData(): Promise<void> {
    try {
      const params = {
        type: MasterType.SIMPLE
      }

      const master = await getGPSTrackerMasterData(params)
      this.gpsTrackerMasterData = (master.list as GPSTrackerMasterData[]) || []
    } catch (e) {
      this.$log.debug('MST_MGMT_TRGTS > fetchAreaAllocationMasterData: e=', e)
      throw e
    }
  }

  /**
   * エリア割当マスタを取得する
   *
   * @returns {void}
   */
  async fetchAreaAllocationMasterData(): Promise<void> {
    try {
      const master = await getAreaMasterDataWhoHasSubPrefecture()
      this.areaAllocationMasterData = (master.areaList as AreaMasterData[]) || []
      this.prefectureMasterData = this.getPrefectureList(this.areaAllocationMasterData)
    } catch (e) {
      this.$log.debug('MST_MGMT_TRGTS > fetchAreaAllocationMasterData: e=', e)
      throw e
    }
  }

  /**
   * 都道府県リストを取得する処理
   *
   * @param {string} areaId エリアID
   * @returns {void}
   */
  getPrefecturesByAreaId(areaId: string): PrefectureMasterData[] {
    const area = this.areaAllocationMasterData.find(element => element.id === areaId)
    if (!area || !area.prefectureList) {
      return []
    }

    return area.prefectureList
  }

  /**
   * 連携会社マスタを取得する
   *
   * @returns {void}
   */
  async fetchConnectMasterData(): Promise<void> {
    try {
      const master = await getConnectCompanyMasterData()
      this.connectCompanyMasterData = master.list as ConnectCompanyMasterData[]
    } catch (e) {
      this.$log.debug('MST_MGMT_TRGTS > fetchConnectMasterData: e=', e)
      throw e
    }
  }

  /**
   * 全都道府県リストを取得する処理
   *
   * @param {AreaMasterData[]} areaAllocationMasterData エリアリスト
   * @returns {PrefectureMasterData[]} 都道府県リスト
   */
  getPrefectureList(areaAllocationMasterData: AreaMasterData[]): PrefectureMasterData[] {
    const prefectureMap = {} as Record<string, PrefectureMasterData>
    areaAllocationMasterData.forEach(item => {
      if (!item.prefectureList) {
        return
      }

      item.prefectureList.forEach(prefecture => {
        if (prefectureMap[prefecture.id]) {
          return
        }

        prefectureMap[prefecture.id] = prefecture
      })
    })

    return Object.values(prefectureMap)
  }

  /**
   * 連結可能トラクターヘッドマスタジャンプ
   *
   * @returns {void}
   */
  onJumpToTrailerSetClick(): void {
    if (!this.checkJump()) {
      return
    }

    // 変更なし
    if (!this.checkDataChanged()) {
      this.handleJump(this.multipleSelection[0])
      return
    }

    const confirmCallback = async () => {
      const multipleSelection = cloneDeep(this.multipleSelection[0])
      if (!(await this.saveTableData())) {
        return
      }

      if (!this.tableData || this.tableData.length === 0) {
        return
      }
      this.handleJump(multipleSelection)
    }

    const rejectCallback = async () => {
      this.handleJump(this.multipleSelection[0])
    }

    msgBoxService.confirmWithCancel(
      i18n.t('messages.masterCommon.saveConfirmWhenChanged').toString(),
      confirmCallback,
      rejectCallback
    )
  }

  /**
   * 連結可能連携会社マスタジャンプ
   *
   * @returns {void}
   */
  onJumpToCompanySetClick(): void {
    // 変更なし
    if (!this.checkDataChanged()) {
      this.handleJumpToConnectCompany()
      return
    }
    const confirmCallback = async () => {
      if (!(await this.saveTableData())) {
        return
      }

      if (!this.tableData || this.tableData.length === 0) {
        return
      }
      this.handleJumpToConnectCompany()
    }

    const rejectCallback = async () => {
      this.handleJumpToConnectCompany()
    }

    msgBoxService.confirmWithCancel(
      i18n.t('messages.masterCommon.saveConfirmWhenChanged').toString(),
      confirmCallback,
      rejectCallback
    )
  }

  checkJump(): boolean {
    if (this.multipleSelection.length === 0) {
      msgBoxService.info(
        i18n.t('vaildate.common.selectRequired', [localService.userInfo?.managementTargetShowName]).toString()
      )

      return false
    }

    // 複数のデータが選択されています
    if (this.multipleSelection.length !== 1) {
      msgBoxService.info(i18n.t('messages.masterCommon.chooseCountMistake', [1]).toString())
      return false
    }

    // 選択した管理対象が新規レコードの場合
    if (!this.multipleSelection[0].id) {
      msgBoxService.info(
        i18n.t('messages.masterCommon.E00030', [localService.userInfo?.managementTargetShowName]).toString()
      )

      return false
    }

    return true
  }

  /**
   * ジャンプ
   *
   * @param {ManagementTargetMasterData} singleSelection 選択したデータ
   * @returns {void}
   */
  handleJump(singleSelection: ManagementTargetMasterData): void {
    moveService.beforePageChange = undefined
    this.$router.push({
      path: config.menu.tractorHeadMaster,
      query: {
        managementTargetId: singleSelection.id,
        managementTargetCode: singleSelection.managementTargetCode,
        managementTargetName: singleSelection.managementTargetName
      }
    })
  }

  /**
   * ジャンプ
   *
   * @returns {void}
   */
  handleJumpToConnectCompany(): void {
    moveService.beforePageChange = undefined
    this.$router.push({
      path: config.menu.connectCompanyMaster,
      query: {
        isJumpedFromManageTarget: '1'
      }
    })
  }

  /**
   * テンプレートボタンをクリックした時の処理
   *
   * @returns {void}
   */
  async onTemplateButtonClick(): Promise<void> {
    const param = {
      templateCode: TemplateCode.MST_MGMT_TRGTS_CONNECT_COMPANY
    }

    const res = await templateDownload(param)
    HelperService.download(res.fileName, res.data)
  }

  /**
   * デジタコ連携会社コード取得
   *
   * @param {string} id デジタコ連携会社ID
   * @returns {string} デジタコ連携会社コード
   */
  getCompanyCodeById(id: string): string {
    if (!id) return ''
    const company = this.connectCompanyMasterData.find(item => item.id === id)
    return company?.companyCode ?? ''
  }

  /**
   * 追加ボタンをクリックした時の処理。
   *
   * @returns {void}
   */
  onInsertButtonClick(): void {
    this.$log.debug('MST_MGMT_TRGTS > onInsertButtonClick')

    const item = {
      ...this.formTemplate,
      dataType: DataType.INSERT,
      areaList: [],
      usageStatus: UsageStatus.IN_USE,
      index: this.tableData.length
    }

    this.tableData.push(item)
    this.visableTableData.push(item)
    this.clearValidate()
    this.insertAfter()
  }

  /**
   * 保存前に、チェックを行う
   *
   * @returns {Promise<boolean>} チェック結果
   */
  protected async saveTableDataBefore(): Promise<boolean> {
    return this.dataValidate()
  }

  /**
   * 保存API
   *
   * @returns {void}
   */
  async saveTableDataAPI(): Promise<void> {
    const params = cloneDeep(this.getUpsertRequest())
    const keys = ['addDataList', 'updateDataList', 'deleteDataList']
    keys.forEach(key => {
      params[key].forEach((item: ManagementTargetMasterData) => {
        if (!item.areaList) {
          return
        }
        item.areaList = item.areaList.map(area => {
          return (area as ManagementTargetArea).areaId
        })
      })
    })
    await updateManagementTargetMasterData(params)
  }

  /**
   * エラー時の処理
   *
   * @returns {void}
   * @param {CommonResponse} commonResponse エラーメッセージ
   */
  handlerSaveTableError(commonResponse: CommonResponse): void {
    if (commonResponse.common.code === 'E00093') {
      this.fetchTableData()
      common.setBussinessOfficeStatus(true)
    }
  }

  /**
   * 出力API
   *
   * @returns {void}
   */
  async tableDataOutputAPI(): Promise<void> {
    const params = {
      businessOfficeId: this.businessOffice,
      type: this.itpFlag ? '1' : '0'
    }

    const res = await managementTargetCSVDownload(params)
    HelperService.download(res.fileName, res.data)
  }

  /**
   * CSVデータを処理する。
   *
   * @param {any[]} dataList データ
   * @returns {void}
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async handleCsvInputData(dataList: any[]): Promise<void> {
    const { messageList, validData } = this.csvValidate(dataList)
    const validDataLength = validData.length
    let msgString = ''

    if (messageList && messageList.length > 0) {
      msgString = HelperService.changeMessageListToString(messageList)
    }

    if (!validData || validDataLength === 0) {
      if (isEmpty(msgString)) {
        msgBoxService.info(i18n.t('messages.masterCommon.fileInserted', [0]).toString())
      } else {
        msgBoxService.info(msgString, undefined, { width: '80vw' } as MessageBoxOption)
      }
      return
    }

    const result = this.csvDataMergeIntoTableDataBefore(validData)

    const batchSize = 100
    const total = result.length

    let processed = 0
    // loading
    loadingService.open(i18n.t('messages.infoDataLoading').toString())
    while (processed < total) {
      await this.processBatch(processed, total, result, batchSize)
      processed += batchSize
    }

    loadingService.close()

    this.baseTableRef.clearSort()

    const option = {
      onClose: (): void => {
        if (messageList && messageList.length > 0) {
          msgBoxService.info(msgString, undefined, { width: '80vw' } as MessageBoxOption)
        }
      }
    } as MessageBoxOption

    msgBoxService.info(i18n.t('messages.masterCommon.fileInserted', [result.length]).toString(), undefined, option)
  }

  // ================================
  // バッチアップロード処理
  // ================================
  processBatch(start: number, total: number, result: ManagementTargetMasterData[], batchSize: number): Promise<void> {
    return new Promise(resolve => {
      const end = Math.min(start + batchSize, total)
      const batch = result.slice(start, end)

      if (batch.length === 0) {
        resolve()
        return
      }

      if (this.showFlag && this.itpFlag) {
        this.mergeTableDataByKey(this.tableData, batch)
        this.mergeTableDataByKey(this.visableTableData, batch)
      } else {
        this.tableData.splice(this.tableData.length, 0, ...batch)
        this.visableTableData.splice(this.visableTableData.length, 0, ...batch)
      }

      // Vueレスポンス更新
      this.$nextTick(() => {
        setTimeout(resolve, 0)
      })
    })
  }

  fieldsToUpdate = [
    'managementTargetName',
    'sigfoxId',
    'usageStatus',
    'owner',
    'lendDate',
    'lendBusinessOfficeId',
    'nextArrangementDate',
    'nextCheckDate',
    'areaList',
    'itpConnectCompanyId'
  ]
  /**
   * 指定されたキーで新しいデータをターゲット配列にマージします：
   * - キー値がすでに存在する場合 → 元のオブジェクトを上書きする
   * - 存在しない場合→末尾に追加
   *
   * @param {any[]} targetArray ターゲット配列（直接変更されます）
   * @param {any[]} newData 新しいデータ配列
   * @param {string} keyField 比較するフィールドの名前（デフォルトは「managementTargetCode」）
   */
  mergeTableDataByKey(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    targetArray: any[],
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    newData: any[],
    keyField: string = 'managementTargetCode' // eslint-disable-line @typescript-eslint/no-inferrable-types
  ): void {
    if (!Array.isArray(targetArray) || !Array.isArray(newData)) {
      this.$log.debug('mergeTableDataByKey: パラメータは配列である必要があります')
      return
    }
    // 既存のデータのキー -> インデックス マッピングを構築する
    const existingMap = new Map<string, number>()
    targetArray.forEach((item, index) => {
      const key = item[keyField]
      if (key != null) {
        existingMap.set(key, index)
      }
    })
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const toAdd: any[] = []
    for (const newItem of newData) {
      const key = newItem[keyField]
      if (key == null) {
        toAdd.push(newItem)
        continue
      }
      const existingIndex = existingMap.get(key)
      if (existingIndex !== undefined) {
        // カバー
        const existingItem = targetArray[existingIndex]
        for (const field of this.fieldsToUpdate) {
          const newValue = newItem[field]
          const oldValue = existingItem[field]
          if (!this.isEqual(oldValue, newValue)) {
            this.$set(existingItem, field, newValue)
          }
        }
      } else {
        // 新しい
        toAdd.push(newItem)
      }
    }
    // バッチ追加
    if (toAdd.length > 0) {
      targetArray.push(...toAdd)
    }
  }

  hasOwn = Object.prototype.hasOwnProperty
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  isEqual(a: any, b: any): boolean {
    if (a === b) return true

    if (a == null || b == null) return a === b

    if (typeof a !== typeof b) return false

    if (typeof a !== 'object') return a === b

    if (Array.isArray(a) !== Array.isArray(b)) return false

    const aKeys = Object.keys(a)
    const bKeys = Object.keys(b)

    if (aKeys.length !== bKeys.length) return false

    for (const key of aKeys) {
      if (!this.hasOwn.call(b, key)) return false
      if (!this.isEqual(a[key], b[key])) return false
    }

    return true
  }

  /**
   * CSVデータ合併前の処理
   *
   * @param {any[]} validData データ
   * @returns {void}
   */
  // eslint-disable-next-line
  csvDataMergeIntoTableDataBefore(validData: any[]): any[] {
    let currentParent = undefined
    const result = []
    for (const data of validData) {
      if (data.managementTargetCode) {
        currentParent = data
        currentParent.areaList = []
        if (this.showFlag && this.itpFlag) {
          const itpConnectCompanyId = find(
            this.connectCompanyMasterData,
            item => item.companyName === data.companyName
          )?.id

          data.itpConnectCompanyId = itpConnectCompanyId
        }

        result.push({
          ...data,
          dataType: DataType.INSERT
        })
      }

      if (!data.area) {
        continue
      }

      const area = {} as ManagementTargetArea
      const areaId = find(this.areaAllocationMasterData, item => item.areaName === data.area)?.id
      area.areaId = areaId || ''
      area.prefectureList = []

      currentParent.areaList.push(area)
    }

    return result
  }

  /**
   * CSVデータをチェックする
   *
   * @param {any[]} csvData データ
   * @returns {any} チェック結果
   */
  // eslint-disable-next-line
  csvValidate(csvData: any[]): any {
    const messageList = []
    const validData = []
    const keys = Object.values(this.csvHeaders)
    let parentIndex = -1

    for (let i = 0; i < csvData.length; i++) {
      let rowErr = false
      const row = csvData[i]
      for (let key of keys) {
        const value = row[key]
        const colName = i18n.t(`master.managementTargetMaster.${key}`, [this.managementTargetShowName]).toString()

        // エリア
        if (key === 'area') {
          if (!value) {
            continue
          }

          // コード変換
          const index = findIndex(this.areaAllocationMasterData, item => item.areaName === value)
          if (index === -1) {
            const message = validateService.getCsvValidateMessage(i + 2, 'vaildate.csvFile.noMasterData', [
              i18n.t('master.managementTargetMaster.areaLabel').toString()
            ])
            messageList.push(message)
            rowErr = true
          }

          // 重複
          if (parentIndex !== -1 && !row['managementTargetCode']) {
            for (let j = parentIndex; j < i; j++) {
              for (let k = j + 1; k <= i; k++) {
                if (csvData[j]['area'] === csvData[k]['area']) {
                  const message = validateService.getCsvValidateMessage(i + 2, 'vaildate.csvFile.repeat', [
                    i18n.t('master.managementTargetMaster.areaLabel').toString()
                  ])
                  messageList.push(message)
                  rowErr = true
                }
              }
            }
          }
        }

        if (parentIndex !== -1) {
          if (!row['managementTargetCode']) {
            continue
          } else {
            parentIndex = -1
          }
        }

        // 管理対象コード
        if (key === 'managementTargetCode') {
          // 必須
          if (!value) {
            const message = validateService.getCsvValidateMessage(i + 2, 'vaildate.csvFile.required', [colName])
            messageList.push(message)
            rowErr = true

            continue
          }

          // 15文字
          if (value.length > 15) {
            const message = validateService.getCsvValidateMessage(i + 2, 'vaildate.csvFile.maxlength', [colName, '15'])
            messageList.push(message)
            rowErr = true
          }

          // 半角英数字
          if (!VALID_PATTERN[RULES.ALPHANUMERIC].test(value)) {
            const message = validateService.getCsvValidateMessage(i + 2, 'vaildate.csvFile.alphanumeric', [
              colName,
              '15'
            ])
            messageList.push(message)
            rowErr = true
          }
          if (!this.showFlag || !this.itpFlag) {
            // 重複
            if (this.checkRepeat(csvData, i, key, value, colName, messageList)) {
              rowErr = true
            }
          }
        }

        // 管理対象名
        if (key === 'managementTargetName') {
          // 必須
          if (!value) {
            const message = validateService.getCsvValidateMessage(i + 2, 'vaildate.csvFile.required', [colName])
            messageList.push(message)
            rowErr = true

            continue
          }

          // 50文字
          if (value.length > 50) {
            const message = validateService.getCsvValidateMessage(i + 2, 'vaildate.csvFile.maxlength', [colName, '50'])
            messageList.push(message)
            rowErr = true
          }
        }

        // sigfoxId
        if (key === 'sigfoxId') {
          if (!value) {
            continue
          }

          // 8文字
          if (value.length !== 8) {
            const message = validateService.getCsvValidateMessage(i + 2, 'vaildate.csvFile.numberWithLength', [
              colName,
              '8'
            ])
            messageList.push(message)
            rowErr = true
          }

          // 半角英数字
          if (!VALID_PATTERN[RULES.ALPHANUMERIC].test(value)) {
            const message = validateService.getCsvValidateMessage(i + 2, 'vaildate.csvFile.alphanumeric', [
              colName,
              '8'
            ])
            messageList.push(message)
            rowErr = true
          }

          // コード変換
          const master = find(this.gpsTrackerMasterData, item => item.sigfoxId === value)
          if (!master) {
            const message = validateService.getCsvValidateMessage(i + 2, 'vaildate.csvFile.noMasterData', [
              colName,
              i18n.t('master.managementTargetMaster.gpstracker').toString()
            ])
            messageList.push(message)
            rowErr = true
          } else {
            row['sigfoxId'] = master.id
          }

          // 重複
          if (this.checkRepeat(csvData, i, key, value, colName, messageList)) {
            rowErr = true
          }
        }

        // 利用状況
        if (key === 'usageStatus') {
          // 必須
          if (!value) {
            const message = validateService.getCsvValidateMessage(i + 2, 'vaildate.csvFile.required', [colName])
            messageList.push(message)
            rowErr = true

            continue
          }

          // コード変換
          const index = findIndex(this.usageStatusList, item => item.code === value)
          if (index === -1) {
            const message = validateService.getCsvValidateMessage(i + 2, 'vaildate.csvFile.tellMasterData', [
              colName,
              '0、1'
            ])
            messageList.push(message)
            rowErr = true
          }
        }

        // 所有者
        if (key === 'owner') {
          if (!value) {
            continue
          }

          // 50文字
          if (value.length > 50) {
            const message = validateService.getCsvValidateMessage(i + 2, 'vaildate.csvFile.maxlength', [colName, '50'])
            messageList.push(message)
            rowErr = true
          }
        }

        // 貸出先営業所コード
        if (key === 'lendBusinessOfficeCode') {
          if (!value) {
            continue
          }

          if (!value && row.lendDate) {
            const message = validateService.getCsvValidateMessage(i + 2, 'vaildate.csvFile.required', [
              colName,
              i18n.t('master.managementTargetMaster.lendBusinessOfficeCode').toString()
            ])
            messageList.push(message)
            rowErr = true
            continue
          }

          const master = find(this.businessOfficeMasterData, item => item.businessOfficeCode === value)
          if (!master) {
            const message = validateService.getCsvValidateMessage(i + 2, 'vaildate.csvFile.noMasterData', [
              colName,
              i18n.t('master.managementTargetMaster.BusinessOfficeMaster').toString()
            ])
            messageList.push(message)
            rowErr = true
          } else {
            row['lendBusinessOfficeId'] = master.id
          }
        }

        // 貸出日
        if (key === 'lendDate') {
          if (!value) {
            continue
          }

          if (!value && row.lendBusinessOfficeCode) {
            const message = validateService.getCsvValidateMessage(i + 2, 'vaildate.csvFile.required', [
              colName,
              i18n.t('master.managementTargetMaster.lendDate').toString()
            ])
            messageList.push(message)
            rowErr = true
            continue
          }

          if (!VALID_PATTERN[RULES.DATE].test(value)) {
            const message = validateService.getCsvValidateMessage(i + 2, 'vaildate.csvFile.date', [colName])
            messageList.push(message)
            rowErr = true
          }
        }

        // 次回定期整備時期日 || 次回車検期日
        if (key === 'nextArrangementDate' || key === 'nextCheckDate') {
          if (!value) {
            continue
          }

          if (!VALID_PATTERN[RULES.DATE].test(value)) {
            const message = validateService.getCsvValidateMessage(i + 2, 'vaildate.csvFile.date', [colName])
            messageList.push(message)
            rowErr = true
          }
        }

        if (this.showFlag && this.itpFlag) {
          // デジタコ連携
          if (key === 'companyName') {
            if (!value) {
              continue
            }

            // 50文字
            if (value.length > 50) {
              const message = validateService.getCsvValidateMessage(i + 2, 'vaildate.csvFile.maxlength', [
                colName,
                '50'
              ])
              messageList.push(message)
              rowErr = true
            }

            // マスターに存在するかどうか
            if (!find(this.connectCompanyMasterData, item => item.companyName === value)) {
              const message = validateService.getCsvValidateMessage(i + 2, 'vaildate.csvFile.noMasterData', [
                value,
                i18n.t('master.managementTargetMaster.connectCompany').toString()
              ])
              messageList.push(message)
              rowErr = true
            }
          }
        }
      }

      if (!rowErr) {
        validData.push(row)
        if (row['managementTargetCode']) {
          parentIndex = i
        }
      } else {
        parentIndex = -1
      }
    }

    return {
      messageList,
      validData
    }
  }

  checkRepeat(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    csvData: any[],
    index: number,
    key: string,
    value: string,
    colName: string,
    messageList: string[]
  ): boolean {
    let rowErr = false
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (0 <= findIndex(this.tableData, (item: any) => item[key] === value)) {
      const message = validateService.getCsvValidateMessage(index + 2, 'vaildate.csvFile.repeat', [colName])
      messageList.push(message)
      rowErr = true
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const first = findIndex(csvData, (item: any) => item[key] === value)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const last = findLastIndex(csvData, (item: any) => item[key] === value)
    if (first !== last) {
      const message = validateService.getCsvValidateMessage(index + 2, 'vaildate.csvFile.repeat', [colName])
      messageList.push(message)
      rowErr = true
    }

    return rowErr
  }

  /**
   * 入力FORMのテンプレートを初期化する
   *
   * @returns {void}
   */
  initFormTemplate(): void {
    this.formTemplate = {
      managementTargetCode: '',
      managementTargetName: '',
      sigfoxId: '',
      usageStatus: '',
      owner: '',
      lendBusinessOfficeId: '',
      lendDate: '',
      nextArrangementDate: '',
      nextCheckDate: '',
      areaId: '',
      itpConnectCompanyId: ''
    }
  }

  // eslint-disable-next-line max-lines-per-function
  initSearchTargets(): void {
    this.searchTargets = [
      { type: FormItemType.Input, field: 'managementTargetCode' },
      { type: FormItemType.Input, field: 'managementTargetName' },
      {
        type: FormItemType.Select,
        field: 'sigfoxId',
        list: () => {
          return this.gpsTrackerMasterData.map(item => {
            return { code: item.id ? item.id : '', name: item.sigfoxId ? item.sigfoxId : '' }
          })
        }
      },
      {
        type: FormItemType.Select,
        field: 'usageStatus',
        list: () => {
          return this.usageStatusList
        }
      },
      { type: FormItemType.Input, field: 'owner' },
      {
        type: FormItemType.Select,
        field: 'lendBusinessOfficeId',
        list: () => {
          return this.allocedBusinessOfficeList.map(item => {
            return {
              code: item.id ? item.id : '',
              name: `${item.businessOfficeName ? item.businessOfficeName : ''}(${
                item.businessOfficeCode ? item.businessOfficeCode : ''
              })`
            }
          })
        }
      },
      { type: FormItemType.Input, field: 'lendDate' },
      { type: FormItemType.Input, field: 'nextArrangementDate' },
      { type: FormItemType.Input, field: 'nextCheckDate' },
      {
        type: FormItemType.Select,
        field: 'areaId',
        getData: (item: ManagementTargetMasterData): string => {
          if (!item.areaList || item.areaList.length === 0) {
            return ''
          }

          let result = ''

          item.areaList.forEach(area => {
            const { areaId, prefectureList } = area as ManagementTargetArea
            const areaFormat = {
              code: 'id',
              value: 'areaName'
            }
            result += HelperService.getDefinitionName(this.areaAllocationMasterData, areaId, areaFormat)

            if (!prefectureList || prefectureList.length === 0) {
              return
            }

            prefectureList.forEach(prefectureId => {
              const prefectureFormat = {
                code: 'id',
                value: 'prefectureName'
              }
              result += HelperService.getDefinitionName(
                this.prefectureMasterData,
                prefectureId as unknown as string,
                prefectureFormat
              )
            })
          })

          return result
        }
      }
    ]

    if (this.showFlag) {
      this.searchTargets.push({
        type: FormItemType.Select,
        field: 'itpConnectCompanyId',
        list: () => {
          return this.connectCompanyMasterData.map(item => {
            return {
              code: item.id ? item.id : '',
              name: `${item.companyName ? item.companyName : ''}(${item.companyCode ? item.companyCode : ''})`
            }
          })
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep .cell {
  text-overflow: unset;
}

::v-deep .tti-bottom-right-area .tti-btn-default {
  margin-right: 0 !important;
}

.tti-main-header-shadow {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
}

.tti-btn-jump {
  background-color: $C43;
  height: 100%;
  float: right;
  padding: 0;

  .svg-icon {
    padding: 0 6px;
  }

  &:active {
    @include bt_001_p;
  }
}

.fileInput {
  display: none;
}

.custom-tree-node {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 14px;
  padding-right: 8px;
}

.areaDelete {
  margin-left: 10px;
}

.areaContainer {
  display: flex;

  .areaInsert {
    padding: 0;
    margin-left: 0 !important;
    margin-right: 4px !important;
  }

  ::v-deep .el-table__empty-block {
    min-height: 30px !important;
    height: 30px !important;
  }

  ::v-deep .el-table__body-wrapper {
    overflow: hidden !important;
  }

  ::v-deep .areaTable {
    padding: 0;

    .tti-btn-003 {
      height: 31px;
    }
  }
}

.connectContainer {
  display: flex;
}

.select-connect-company {
  width: 300px !important;
}

.company-code {
  display: inline-block;
  height: 32px;
  width: 200px;
  border: 1px solid #dcdfe6;
  padding: 3px;
}

.connect-template {
  width: 220px;
}

.connect-upload {
  width: 200px;
}
</style>
